import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { getLocale } from "react-i18nify";
import { useSnackbar } from "notistack";
import * as MatStyled from "@mui/material/styles";

import {
  getSellerDetail,
  approveUpdateSeller,
  rejectUpdateSeller,
  updateSellerByAdmin,
} from "src/api/seller";

import { device } from "src/styles/device";
import { checkNotEmpty, downloadImage } from "src/common";
import { decryptData } from "src/components/profile/DecryptData";
import { useDialog } from "src/context/DialogContext";
import { useAuth } from "src/hooks/useAuth";

import { Grid } from "@mui/material";
import TextField from "@mui/material/TextField";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import Divider from "@mui/material/Divider";

import { Delete, PhotoCameraOutlined } from "@mui/icons-material";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";

import Forbidden from "src/pages/Forbidden";
import ModalFullImage from "src/components/ui/ModalFullImage";
import DropZone from "src/components/core/DropZone";
import StyledButton from "src/components/core/Button";

import AddLocationDialog from "src/components/seller/AddLocationDialog";
import TermsComponent from "src/components/seller/Terms";

/* ─── Layout ─────────────────────────────────────────────── */

const PageContainer = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: auto;
  padding: 32px 40px 64px;

  @media ${device.tabletL} {
    padding: 24px 20px 48px;
  }
`;

/* ─── Page Header ─────────────────────────────────────────── */

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #252525;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 28px;
    background: #e13b30;
    border-radius: 3px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
`;

/* ─── Cards ───────────────────────────────────────────────── */

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #ebebed;
  border-radius: 20px;
  padding: 28px 32px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media ${device.tabletL} {
    padding: 20px;
  }
`;

const CardTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #252525;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    display: inline-block;
    width: 3px;
    height: 18px;
    background: #e13b30;
    border-radius: 2px;
  }
`;

/* ─── Stat Cards ──────────────────────────────────────────── */

const StatCard = styled.div`
  background: #ffffff;
  border: 1px solid #ebebed;
  border-radius: 16px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
`;

const StatIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fdf0ef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatLabel = styled.p`
  font-size: 0.75rem;
  color: #6c6c70;
  margin: 0 0 4px;
`;

const StatValue = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: #252525;
  margin: 0;
`;

/* ─── Form Fields ─────────────────────────────────────────── */

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 500;
  color: #6c6c70;
  margin: 0 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const FieldValue = styled.p`
  font-size: 0.9375rem;
  color: #252525;
  margin: 0;
  word-break: break-word;
  line-height: 1.55;
`;

const FieldValueDesc = styled.p`
  font-size: 0.9375rem;
  color: #252525;
  margin: 0;
  word-break: break-word;
  line-height: 1.55;
  max-height: 130px;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 5px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: #d8d8dc;
    border-radius: 100px;
  }
`;

const WrapperTextfield = styled.div`
  width: 100%;

  .MuiOutlinedInput-input {
    background: #fff;
    border-radius: 12px;
    padding: 10px 14px;
  }
  .MuiOutlinedInput-root {
    border-radius: 12px;
  }
  .MuiTextField-root {
    border-radius: 12px;
  }
  .MuiFormHelperText-root {
    color: #e13b30;
  }
`;

/* ─── Image Upload / Preview ──────────────────────────────── */

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const ImageItem = styled.div`
  background: #f8f8f9;
  border: 1px dashed #d8d8dc;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
`;

const ImageItemInner = styled.div`
  aspect-ratio: 4/3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageMeta = styled.div`
  padding: 10px 14px 12px;
`;

const ImageMetaLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 500;
  color: #6c6c70;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0 0 2px;
`;

const ImageMetaDesc = styled.p`
  font-size: 0.8125rem;
  color: #252525;
  margin: 0;
  line-height: 1.4;
`;

const DeleteBtn = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: background 0.15s;

  &:hover {
    background: #fdf0ef;
  }

  svg {
    font-size: 1rem;
    color: #e04132;
  }
`;

const UploadPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  padding: 12px;

  svg {
    color: #bcbcc0;
    font-size: 1.75rem;
  }

  span {
    font-size: 0.75rem;
    color: #6c6c70;
  }

  &:hover svg {
    color: #e13b30;
  }
`;

const FileTip = styled.span`
  font-size: 0.625rem;
  color: #e13b30;
  text-align: center;
  margin-top: 2px;
  line-height: 1.4;
`;

const ViewFullLink = styled.span`
  display: block;
  text-align: center;
  font-size: 0.7rem;
  color: #6c6c70;
  padding: 4px 0 8px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #e13b30;
  }
`;

const PdfIconWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 6px;
  padding: 16px;
`;

const DownloadPdfLink = styled.span`
  font-size: 0.75rem;
  color: #252525;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #e13b30;
  }
`;

const WrapperOtherImage = styled.div`
  width: 100%;
  height: 220px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media ${device.tabletL} {
    height: 150px;
  }
  @media ${device.mobile} {
    height: 100px;
  }
`;

const ImageOtherEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 220px;
  background: #f8f8f9;
  border: 1px dashed #d8d8dc;
  border-radius: 16px;

  @media ${device.tabletL} {
    height: 150px;
  }
  @media ${device.mobile} {
    height: 100px;
  }
`;

const GalleryLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 500;
  color: #6c6c70;
  margin: 6px 0 0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const SpanReject = styled.span`
  color: rgb(194, 43, 29);
  font-size: 1.25rem;
  font-weight: 600;
  display: block;
  margin-bottom: 8px;
`;

const WrapperTextField = styled.div`
  width: 100%;
  margin-bottom: 16px;

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
  }
`;

const StyledAccordion = styled(Accordion)`
  border-radius: 20px !important;
  padding: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;

  &.MuiAccordion-root {
    background-color: #ffffff;
    border: 1px solid #ebebed;
    border-radius: 20px !important;
  }

  &::before {
    display: none;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  &.MuiAccordionSummary-root.Mui-expanded {
    background-color: #fff;
    border-radius: 14px;
  }
`;

const transformPdfUrl = (url) => {
  if (typeof url !== "string") return url;
  return url
    .replace(
      /devimage\.xsurface\.com/,
      "devx-test-image.s3-ap-southeast-1.amazonaws.com",
    )
    .replace(
      /productimage\.xsurface\.com/,
      "product-xsurface.s3.ap-southeast-1.amazonaws.com",
    );
};

const isPdfValue = (value) => {
  if (!value) return false;
  if (typeof value === "string") return value.toLowerCase().includes(".pdf");
  if (value instanceof File)
    return (
      value.type === "application/pdf" ||
      value.name?.toLowerCase().endsWith(".pdf")
    );
  return false;
};

const imgSrc = (value) =>
  typeof value === "string" ? value : URL.createObjectURL(value);

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB

const MixedUploadCard = ({ value, onDrop, onDelete, label, description }) => {
  const hasValue = checkNotEmpty(value);
  const isPdf = isPdfValue(value);

  const handleDrop = (files) => {
    const file = files[0];
    if (file && file.size > MAX_FILE_SIZE) return;
    onDrop(files);
  };

  return (
    <ImageItem>
      {hasValue ? (
        <>
          <ImageItemInner
            style={{
              minHeight: "140px",
              ...(isPdf ? { background: "#f8f8f9" } : {}),
            }}
          >
            {isPdf ? (
              <PdfIconWrap>
                <TextSnippetIcon
                  sx={{ fontSize: "2.5rem", color: "#e13b30" }}
                />
              </PdfIconWrap>
            ) : (
              <img src={imgSrc(value)} alt={label} />
            )}
            <DeleteBtn onClick={onDelete}>
              <Delete />
            </DeleteBtn>
          </ImageItemInner>
        </>
      ) : (
        <DropZone onDrop={handleDrop} accept=".jpg, .jpeg, .png, .webp, .pdf">
          {(props) => (
            <ImageItemInner style={{ minHeight: "140px" }}>
              <UploadPlaceholder {...props}>
                <PhotoCameraOutlined />
                <span>อัพโหลดรูปภาพ / PDF</span>
                <FileTip>.jpg .jpeg .png .webp .pdf • ไม่เกิน 4 MB</FileTip>
              </UploadPlaceholder>
            </ImageItemInner>
          )}
        </DropZone>
      )}
      <ImageMeta>
        <ImageMetaLabel>{label}</ImageMetaLabel>
        <ImageMetaDesc>{description}</ImageMetaDesc>
      </ImageMeta>
    </ImageItem>
  );
};

const MixedViewCard = ({ value, label, description, onView }) => {
  const hasValue = checkNotEmpty(value);
  const isPdf = isPdfValue(value);

  const handlePdfDownload = () => {
    if (typeof value === "string") {
      downloadImage(transformPdfUrl(value));
    }
  };

  return (
    <ImageItem>
      <ImageItemInner
        style={
          !hasValue || isPdf
            ? { background: "#f8f8f9", minHeight: "140px" }
            : { minHeight: "140px" }
        }
      >
        {hasValue ? (
          isPdf ? (
            <PdfIconWrap>
              <TextSnippetIcon sx={{ fontSize: "2.5rem", color: "#e13b30" }} />
              <DownloadPdfLink onClick={handlePdfDownload}>
                Download PDF
              </DownloadPdfLink>
            </PdfIconWrap>
          ) : (
            <img
              src={typeof value === "string" ? value : imgSrc(value)}
              alt={label}
            />
          )
        ) : (
          <InsertPhotoIcon sx={{ color: "#d8d8dc", fontSize: "2.5rem" }} />
        )}
      </ImageItemInner>
      {hasValue && !isPdf && onView && typeof value === "string" && (
        <ViewFullLink onClick={onView}>
          คลิกเพิ่มเติมเพื่อดูรูปเต็ม
        </ViewFullLink>
      )}
      <ImageMeta>
        <ImageMetaLabel>{label}</ImageMetaLabel>
        <ImageMetaDesc>{description}</ImageMetaDesc>
      </ImageMeta>
    </ImageItem>
  );
};

const drawerWidth = 260;

const Main = MatStyled.styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

const SellerDetail = ({ open }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, checkAllowPermission } = useAuth();
  let { sellerId } = useParams();
  const navigate = useNavigate();
  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const [isEdit, setIsEdit] = useState(false);
  const [comment, setComment] = useState("");
  const [isRemark, setIsRemark] = useState(false);
  const [sellerData, setSellerData] = useState({});

  useEffect(() => {
    if (sellerId === undefined) return;

    fetchSellerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  const fetchSellerData = async () => {
    await getSellerDetail(sellerId)
      .then(async ({ data }) => {
        const decrypt = await decryptData(data);
        // console.log("----- seller -----", decrypt);
        setSellerData({
          ...decrypt,
          profile_image: decrypt?.profile_image || "",
          warehouse_image: decrypt?.warehouse_image || "",
          registation: decrypt?.registation || "",
          banner_image: decrypt?.banner_image || "",
          id_card_image: decrypt?.id_card_image || "",
          book_bank_image: decrypt?.book_bank_image || "",
          work_image_first: decrypt?.work_image_first || "",
          work_image_second: decrypt?.work_image_second || "",
          work_image_third: decrypt?.work_image_third || "",
          work_image_fourth: decrypt?.work_image_fourth || "",
          work_image_fifth: decrypt?.work_image_fifth || "",
          work_image_sixth: decrypt?.work_image_sixth || "",
        });
      })
      .catch((err) => {
        console.log("error", err.message);
        navigate("/404");
      });
  };

  const handleSaveApprove = async () => {
    try {
      let params = {
        id: sellerId,
      };

      await approveUpdateSeller(params);

      enqueueSnackbar(`บันทึกสำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`เกิดข้อผิดพลาด บันทึกไม่สำเร็จ`, {
        variant: "error",
      });
    } finally {
      fetchSellerData();
      setIsEdit(false);
    }
  };

  const handleSaveReject = async () => {
    try {
      let params = {
        id: sellerId,
        message: comment,
      };

      await rejectUpdateSeller(params);

      enqueueSnackbar(`ปฏิเสธผู้ขายสำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`เกิดข้อผิดพลาด ปฏิเสธผู้ขายไม่สำเร็จ`, {
        variant: "error",
      });
    } finally {
      fetchSellerData();
      setIsEdit(false);
    }
  };

  // const handleUpdateSellerMedia = async () => {
  //   try {
  //     const payload = {
  //       icon_homepage: sellerData?.icon_homepage,
  //       book_bank_image: sellerData?.book_bank_image,
  //     };

  //     await updateSellerMedia(sellerId, payload); // only update category available

  //     enqueueSnackbar(`บันทึกสำเร็จ`, {
  //       variant: "success",
  //     });

  //     fetchSellerData();
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };

  const handleUpdateSeller = async () => {
    try {
      await updateSellerByAdmin(sellerId, sellerData);

      enqueueSnackbar(`บันทึกสำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
    } finally {
      fetchSellerData();
      setIsEdit(false);
    }
  };

  const PartnerType = (type) => {
    if (type === 1) {
      return <> {getLocale() === "th" ? "โรงงาน" : "Manufactory"}</>;
    } else if (type === 2) {
      return <> {getLocale() === "th" ? "เจ้าของร้าน" : "Store owner"}</>;
    } else if (type === 3) {
      return <>{getLocale() === "th" ? "ผู้จัดจำหน่าย" : "Distributor"}</>;
    } else if (type === 4) {
      return <>{getLocale() === "th" ? "ร้านค้าปลีก" : "Retailer"}</>;
    } else {
      return <> {getLocale() === "th" ? "ตัวแทนขาย" : "Sale representative"}</>;
    }
  };

  if (user?.role_id === 3) return <Forbidden />;

  return (
    <Main
      sx={{ marginTop: "75px", background: "#f7f7f8", width: "100%" }}
      open={open}
    >
      <PageContainer>
        {/* ─── Page Header ─── */}
        <PageHeader>
          <PageTitle>
            {getLocale() === "th" ? "โปรไฟล์ของผู้ขาย" : "Seller Profile"}
          </PageTitle>

          <HeaderActions>
            {checkAllowPermission("sellers.can_edit") ? (
              isEdit ? (
                <>
                  <StyledButton
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={() =>
                      openDialog({
                        type: "confirm",
                        title: "คุณต้องการยกเลิกการแก้ไขใช่หรือไม่",
                        onConfirm: () => {
                          setIsEdit(false);
                          closeDialog();
                        },
                        maxWidth: 342,
                      })
                    }
                  >
                    {getLocale() === "th" ? "ยกเลิก" : "Cancel"}
                  </StyledButton>
                  <StyledButton
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() =>
                      openDialog({
                        type: "confirm",
                        title: "คุณต้องการบันทึกการแก้ไขใช่หรือไม่",
                        onConfirm: () => {
                          handleUpdateSeller();
                          closeDialog();
                        },
                        maxWidth: 342,
                      })
                    }
                  >
                    {getLocale() === "th" ? "บันทึก" : "Save"}
                  </StyledButton>
                </>
              ) : (
                <StyledButton
                  variant="secondary"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEdit(true)}
                >
                  {getLocale() === "th" ? "แก้ไขข้อมูล" : "Edit profile"}
                </StyledButton>
              )
            ) : null}
          </HeaderActions>
        </PageHeader>

        {/* ─── Account Info + Stats ─── */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardTitle>
                {getLocale() === "th" ? "ข้อมูลบัญชี" : "Account Information"}
              </CardTitle>

              {isEdit ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FieldLabel>
                      {getLocale() === "th" ? "ชื่อบริษัท" : "Company name"}
                    </FieldLabel>
                    <WrapperTextfield>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={sellerData?.company_name || ""}
                        onChange={(e) =>
                          setSellerData({
                            ...sellerData,
                            company_name: e.target.value,
                          })
                        }
                        error={!checkNotEmpty(sellerData?.company_name)}
                        helperText={
                          !checkNotEmpty(sellerData?.company_name)
                            ? "โปรดระบุชื่อบริษัท"
                            : "ชื่อบริษัทหรือชื่อร้านค้าที่ปรากฏในระบบ"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldLabel>
                      {getLocale() === "th" ? "เบอร์โทรศัพท์" : "Phone number"}
                    </FieldLabel>
                    <WrapperTextfield>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={sellerData?.phone_number || ""}
                        onChange={(e) =>
                          setSellerData({
                            ...sellerData,
                            phone_number: e.target.value,
                          })
                        }
                        error={!checkNotEmpty(sellerData?.phone_number)}
                        helperText={
                          !checkNotEmpty(sellerData?.phone_number)
                            ? "โปรดระบุเบอร์โทรศัพท์"
                            : "เบอร์ติดต่อ เช่น 02-xxx-xxxx หรือ 08x-xxx-xxxx"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>

                  <Grid item xs={12}>
                    <FieldLabel>
                      {getLocale() === "th" ? "อีเมล" : "Email address"}
                    </FieldLabel>
                    <FieldValue style={{ color: "#6c6c70" }}>
                      {sellerData?.email === "" ||
                      sellerData?.email === "null" ||
                      sellerData?.email === null ||
                      sellerData?.email === undefined
                        ? "-"
                        : sellerData?.email}
                    </FieldValue>
                  </Grid>

                  <Grid item xs={12}>
                    <FieldLabel>
                      {getLocale() === "th"
                        ? "ที่อยู่บริษัท"
                        : "Company address"}
                    </FieldLabel>
                    <WrapperTextfield>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={sellerData?.address || ""}
                        onChange={(e) =>
                          setSellerData({
                            ...sellerData,
                            address: e.target.value,
                          })
                        }
                        error={!checkNotEmpty(sellerData?.address)}
                        helperText={
                          !checkNotEmpty(sellerData?.address)
                            ? "โปรดระบุที่อยู่บริษัท"
                            : "ที่อยู่บริษัทหรือสถานประกอบการ"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>

                  <Grid item xs={12}>
                    <FieldLabel>
                      {getLocale() === "th"
                        ? "เกี่ยวกับบริษัท"
                        : "About company"}
                    </FieldLabel>
                    <WrapperTextfield>
                      <TextField
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        value={sellerData?.description || ""}
                        onChange={(e) =>
                          setSellerData({
                            ...sellerData,
                            description: e.target.value,
                          })
                        }
                        error={!checkNotEmpty(sellerData?.description)}
                        helperText={
                          !checkNotEmpty(sellerData?.description)
                            ? "โปรดระบุเกี่ยวกับบริษัท"
                            : "แนะนำบริษัท สินค้า หรือบริการของคุณโดยย่อ"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FieldGroup>
                      <FieldLabel>
                        {getLocale() === "th" ? "ชื่อบริษัท" : "Company name"}
                      </FieldLabel>
                      <FieldValue>
                        {sellerData?.company_name === "" ||
                        sellerData?.company_name === "null" ||
                        sellerData?.company_name === null
                          ? "-"
                          : sellerData?.company_name}
                      </FieldValue>
                    </FieldGroup>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldGroup>
                      <FieldLabel>
                        {getLocale() === "th"
                          ? "เบอร์โทรศัพท์"
                          : "Phone number"}
                      </FieldLabel>
                      <FieldValue>
                        {sellerData?.phone_number === "" ||
                        sellerData?.phone_number === "null" ||
                        sellerData?.phone_number === null
                          ? "-"
                          : sellerData?.phone_number}
                      </FieldValue>
                    </FieldGroup>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldGroup>
                      <FieldLabel>
                        {getLocale() === "th" ? "อีเมล" : "Email address"}
                      </FieldLabel>
                      <FieldValue>
                        {sellerData?.email === "" ||
                        sellerData?.email === "null" ||
                        sellerData?.email === null ||
                        sellerData?.email === undefined
                          ? "-"
                          : sellerData?.email}
                      </FieldValue>
                    </FieldGroup>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldGroup>
                      <FieldLabel>
                        {getLocale() === "th"
                          ? "ที่อยู่บริษัท"
                          : "Company address"}
                      </FieldLabel>
                      <FieldValue>
                        {sellerData?.address === "" ||
                        sellerData?.address === "null" ||
                        sellerData?.address === null ||
                        sellerData?.address === undefined
                          ? "-"
                          : sellerData?.address}
                      </FieldValue>
                    </FieldGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <FieldGroup>
                      <FieldLabel>
                        {getLocale() === "th"
                          ? "เกี่ยวกับบริษัท"
                          : "About company"}
                      </FieldLabel>
                      <FieldValueDesc>
                        {sellerData?.description === "" ||
                        sellerData?.description === "null" ||
                        sellerData?.description === null ||
                        sellerData?.description === undefined
                          ? "-"
                          : sellerData?.description}
                      </FieldValueDesc>
                    </FieldGroup>
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 2 }} />

              <FieldLabel sx={{ mb: 1 }}>
                {getLocale() === "th"
                  ? "ตำแหน่งที่ตั้งบริษัท"
                  : "Company location"}
              </FieldLabel>
              <StyledButton
                variant="secondary"
                startIcon={<LocationOnIcon />}
                onClick={() =>
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <AddLocationDialog
                        sellerDetail={sellerData}
                        onClose={() => closeDialog()}
                        onSuccess={() => {
                          closeAllDialogs();
                          fetchSellerData();
                        }}
                      />
                    ),
                  })
                }
              >
                {getLocale() === "th"
                  ? "จัดการตำแหน่งที่ตั้งบริษัท"
                  : "Manage company location"}
              </StyledButton>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard>
              <StatIconWrap>
                <Inventory2OutlinedIcon
                  sx={{ color: "#e13b30", fontSize: "1.35rem" }}
                />
              </StatIconWrap>
              <div>
                <StatLabel>
                  {getLocale() === "th" ? "จำนวนสินค้า" : "Products"}
                </StatLabel>
                <StatValue>{sellerData?.productCount ?? "-"}</StatValue>
              </div>
            </StatCard>

            <StatCard>
              <StatIconWrap>
                <CalendarTodayOutlinedIcon
                  sx={{ color: "#e13b30", fontSize: "1.35rem" }}
                />
              </StatIconWrap>
              <div>
                <StatLabel>
                  {getLocale() === "th" ? "วันที่เข้าร่วม" : "Join date"}
                </StatLabel>
                <StatValue>
                  {sellerData?.createdAt
                    ? moment(sellerData.createdAt).format("L")
                    : "-"}
                </StatValue>
              </div>
            </StatCard>

            <StatCard>
              <StatIconWrap>
                <BusinessCenterOutlinedIcon
                  sx={{ color: "#e13b30", fontSize: "1.35rem" }}
                />
              </StatIconWrap>
              <div>
                <StatLabel>
                  {getLocale() === "th" ? "ประเภทธุรกิจ" : "Business type"}
                </StatLabel>
                <StatValue>{PartnerType(sellerData?.seller_type)}</StatValue>
              </div>
            </StatCard>
          </Grid>
        </Grid>

        {/* ─── Images & Documents ─── */}
        <Card>
          <CardTitle>
            {getLocale() === "th" ? "รูปภาพและเอกสาร" : "Images & Documents"}
          </CardTitle>

          <ImageGrid>
            {/* HIDDEN: icon_homepage
            {isEdit ? (
              <ImageItem>
                {checkNotEmpty(sellerData?.icon_homepage) ? (
                  <>
                    <ImageItemInner>
                      <img
                        src={
                          typeof sellerData.icon_homepage === "string"
                            ? sellerData.icon_homepage
                            : URL.createObjectURL(sellerData.icon_homepage)
                        }
                        alt="icon homepage"
                      />
                      <DeleteBtn
                        onClick={() =>
                          setSellerData({ ...sellerData, icon_homepage: "" })
                        }
                      >
                        <Delete />
                      </DeleteBtn>
                    </ImageItemInner>
                  </>
                ) : (
                  <DropZone
                    onDrop={(files) =>
                      setSellerData({
                        ...sellerData,
                        icon_homepage: files[0],
                      })
                    }
                    accept=".jpg, .jpeg, .png, .webp"
                  >
                    {(props) => (
                      <ImageItemInner style={{ minHeight: "140px" }}>
                        <UploadPlaceholder {...props}>
                          <PhotoCameraOutlined />
                          <span>อัพโหลดรูปภาพ</span>
                          <FileTip>.jpg .jpeg .png .webp</FileTip>
                        </UploadPlaceholder>
                      </ImageItemInner>
                    )}
                  </DropZone>
                )}
                <ImageMeta>
                  <ImageMetaLabel>
                    {getLocale() === "th" ? "ไอคอนหน้าเว็บ" : "Homepage icon"}
                  </ImageMetaLabel>
                  <ImageMetaDesc>
                    {getLocale() === "th"
                      ? "รูปภาพไอค่อนหน้าเว็บไซต์"
                      : "Image of icon homepage"}
                  </ImageMetaDesc>
                </ImageMeta>
              </ImageItem>
            ) : (
              <ImageItem>
                <ImageItemInner
                  style={
                    !checkNotEmpty(sellerData?.icon_homepage)
                      ? { background: "#f8f8f9", minHeight: "140px" }
                      : { minHeight: "140px" }
                  }
                >
                  {checkNotEmpty(sellerData?.icon_homepage) ? (
                    <img
                      src={
                        typeof sellerData.icon_homepage === "string"
                          ? sellerData.icon_homepage
                          : URL.createObjectURL(sellerData.icon_homepage)
                      }
                      alt="icon homepage"
                    />
                  ) : (
                    <InsertPhotoIcon
                      sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                    />
                  )}
                </ImageItemInner>
                {checkNotEmpty(sellerData?.icon_homepage) &&
                  typeof sellerData.icon_homepage === "string" && (
                    <ViewFullLink
                      onClick={() =>
                        openDialog({
                          type: "custom",
                          customDialog: (
                            <ModalFullImage
                              image={sellerData.icon_homepage}
                              close={() => closeAllDialogs()}
                            />
                          ),
                        })
                      }
                    >
                      คลิกเพิ่มเติมเพื่อดูรูปเต็ม
                    </ViewFullLink>
                  )}
                <ImageMeta>
                  <ImageMetaLabel>
                    {getLocale() === "th" ? "ไอคอนหน้าเว็บ" : "Homepage icon"}
                  </ImageMetaLabel>
                  <ImageMetaDesc>
                    {getLocale() === "th"
                      ? "รูปภาพไอค่อนหน้าเว็บไซต์"
                      : "Image of icon homepage"}
                  </ImageMetaDesc>
                </ImageMeta>
              </ImageItem>
            )}
            */}

            {/* profile_image */}
            {isEdit ? (
              <ImageItem>
                {checkNotEmpty(sellerData?.profile_image) ? (
                  <>
                    <ImageItemInner>
                      <img
                        src={
                          typeof sellerData.profile_image === "string"
                            ? sellerData.profile_image
                            : URL.createObjectURL(sellerData.profile_image)
                        }
                        alt="profile_image"
                      />
                      <DeleteBtn
                        onClick={() =>
                          setSellerData({ ...sellerData, profile_image: "" })
                        }
                      >
                        <Delete />
                      </DeleteBtn>
                    </ImageItemInner>
                  </>
                ) : (
                  <DropZone
                    onDrop={(files) =>
                      setSellerData({
                        ...sellerData,
                        profile_image: files[0],
                      })
                    }
                    accept=".jpg, .jpeg, .png, .webp"
                  >
                    {(props) => (
                      <ImageItemInner style={{ minHeight: "140px" }}>
                        <UploadPlaceholder {...props}>
                          <PhotoCameraOutlined />
                          <span>อัพโหลดรูปภาพ</span>
                          <FileTip>.jpg .jpeg .png .webp</FileTip>
                        </UploadPlaceholder>
                      </ImageItemInner>
                    )}
                  </DropZone>
                )}
                <ImageMeta>
                  <ImageMetaLabel>
                    {getLocale() === "th" ? "โลโก้ร้านค้า" : "Store / Logo"}
                  </ImageMetaLabel>
                  <ImageMetaDesc>
                    {getLocale() === "th"
                      ? "รูปภาพโลโก้ร้านค้า"
                      : "Image of store or logo"}
                  </ImageMetaDesc>
                </ImageMeta>
              </ImageItem>
            ) : (
              <ImageItem>
                <ImageItemInner
                  style={
                    !checkNotEmpty(sellerData?.profile_image)
                      ? { background: "#f8f8f9", minHeight: "140px" }
                      : { minHeight: "140px" }
                  }
                >
                  {checkNotEmpty(sellerData?.profile_image) ? (
                    <img src={sellerData.profile_image} alt="profile_image" />
                  ) : (
                    <InsertPhotoIcon
                      sx={{ color: "#d8d8dc", fontSize: "2.5rem" }}
                    />
                  )}
                </ImageItemInner>
                {checkNotEmpty(sellerData?.profile_image) && (
                  <ViewFullLink
                    onClick={() =>
                      openDialog({
                        type: "custom",
                        customDialog: (
                          <ModalFullImage
                            image={sellerData.profile_image}
                            close={() => closeAllDialogs()}
                          />
                        ),
                      })
                    }
                  >
                    คลิกเพิ่มเติมเพื่อดูรูปเต็ม
                  </ViewFullLink>
                )}
                <ImageMeta>
                  <ImageMetaLabel>
                    {getLocale() === "th" ? "โลโก้ร้านค้า" : "Store / Logo"}
                  </ImageMetaLabel>
                  <ImageMetaDesc>
                    {getLocale() === "th"
                      ? "รูปภาพโลโก้ร้านค้า"
                      : "Image of store or logo"}
                  </ImageMetaDesc>
                </ImageMeta>
              </ImageItem>
            )}

            {/* id_card_image */}
            {isEdit ? (
              <MixedUploadCard
                value={sellerData?.id_card_image}
                onDrop={(files) =>
                  setSellerData({ ...sellerData, id_card_image: files[0] })
                }
                onDelete={() =>
                  setSellerData({ ...sellerData, id_card_image: "" })
                }
                label={
                  getLocale() === "th"
                    ? "สำเนาบัตร/ใบทะเบียน"
                    : "ID / Registration"
                }
                description={
                  getLocale() === "th"
                    ? "สำเนาใบทะเบียนภาษีมูลค่าเพิ่ม (ภ.พ.20) หรือสำเนาบัตรประชาชน"
                    : "A copy of company registration or ID card"
                }
              />
            ) : (
              <MixedViewCard
                value={sellerData?.id_card_image}
                label={
                  getLocale() === "th"
                    ? "สำเนาบัตร/ใบทะเบียน"
                    : "ID / Registration"
                }
                description={
                  getLocale() === "th"
                    ? "สำเนาใบทะเบียนภาษีมูลค่าเพิ่ม (ภ.พ.20) หรือสำเนาบัตรประชาชน"
                    : "A copy of company registration or ID card"
                }
                onView={() =>
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <ModalFullImage
                        image={sellerData.id_card_image}
                        close={() => closeAllDialogs()}
                      />
                    ),
                  })
                }
              />
            )}

            {/* warehouse_image */}
            {isEdit ? (
              <MixedUploadCard
                value={sellerData?.warehouse_image}
                onDrop={(files) =>
                  setSellerData({ ...sellerData, warehouse_image: files[0] })
                }
                onDelete={() =>
                  setSellerData({ ...sellerData, warehouse_image: "" })
                }
                label={
                  getLocale() === "th"
                    ? "ภาพหน้าร้าน/โรงงาน"
                    : "Store / Factory front"
                }
                description={
                  getLocale() === "th"
                    ? "รูปภาพหน้าร้านค้าหรือภาพสินค้าที่เกี่ยวข้องเพื่อยืนยันตัวตน"
                    : "A photo of company, factory or store front in order to verify"
                }
              />
            ) : (
              <MixedViewCard
                value={sellerData?.warehouse_image}
                label={
                  getLocale() === "th"
                    ? "ภาพหน้าร้าน/โรงงาน"
                    : "Store / Factory front"
                }
                description={
                  getLocale() === "th"
                    ? "รูปภาพหน้าร้านค้าหรือภาพสินค้าที่เกี่ยวข้องเพื่อยืนยันตัวตน"
                    : "A photo of company, factory or store front in order to verify"
                }
                onView={() =>
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <ModalFullImage
                        image={sellerData.warehouse_image}
                        close={() => closeAllDialogs()}
                      />
                    ),
                  })
                }
              />
            )}

            {/* book_bank_image */}
            {isEdit ? (
              <MixedUploadCard
                value={sellerData?.book_bank_image}
                onDrop={(files) =>
                  setSellerData({ ...sellerData, book_bank_image: files[0] })
                }
                onDelete={() =>
                  setSellerData({ ...sellerData, book_bank_image: "" })
                }
                label={getLocale() === "th" ? "สมุดบัญชีธนาคาร" : "Bank book"}
                description={
                  getLocale() === "th"
                    ? "รูปภาพสมุดบัญชี"
                    : "Image of book bank"
                }
              />
            ) : (
              <MixedViewCard
                value={sellerData?.book_bank_image}
                label={getLocale() === "th" ? "สมุดบัญชีธนาคาร" : "Bank book"}
                description={
                  getLocale() === "th"
                    ? "รูปภาพสมุดบัญชี"
                    : "Image of book bank"
                }
                onView={() =>
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <ModalFullImage
                        image={sellerData.book_bank_image}
                        close={() => closeAllDialogs()}
                      />
                    ),
                  })
                }
              />
            )}

            {/* registation (PDF / Image) */}
            {isEdit ? (
              <MixedUploadCard
                value={sellerData?.registation}
                onDrop={(files) =>
                  setSellerData({ ...sellerData, registation: files[0] })
                }
                onDelete={() =>
                  setSellerData({ ...sellerData, registation: "" })
                }
                label={
                  getLocale() === "th" ? "เอกสารร้านค้า" : "Store documents"
                }
                description={
                  getLocale() === "th"
                    ? "เอกสารสำเนาเกี่ยวข้องกับร้านค้า"
                    : "Copy of documents related to the store"
                }
              />
            ) : (
              <MixedViewCard
                value={sellerData?.registation}
                label={
                  getLocale() === "th" ? "เอกสารร้านค้า" : "Store documents"
                }
                description={
                  getLocale() === "th"
                    ? "เอกสารสำเนาเกี่ยวข้องกับร้านค้า"
                    : "Copy of documents related to the store"
                }
                onView={() =>
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <ModalFullImage
                        image={sellerData.registation}
                        close={() => closeAllDialogs()}
                      />
                    ),
                  })
                }
              />
            )}
          </ImageGrid>
        </Card>

        {/* ─── Gallery Images ─── */}
        <Card>
          <CardTitle>
            {getLocale() === "th" ? "รูปภาพแกลเลอรี่" : "Gallery Images"}
          </CardTitle>

          <Grid container spacing={2}>
            {[
              {
                key: "banner_image",
                label: getLocale() === "th" ? "แบนเนอร์" : "Banner",
                size: 6,
              },
              {
                key: "work_image_first",
                label: getLocale() === "th" ? "ภาพผลงาน 1" : "Work image 1",
                size: 6,
              },
              {
                key: "work_image_second",
                label: getLocale() === "th" ? "ภาพผลงาน 2" : "Work image 2",
                size: 6,
              },
              {
                key: "work_image_third",
                label: getLocale() === "th" ? "ภาพผลงาน 3" : "Work image 3",
                size: 6,
              },
              {
                key: "work_image_fourth",
                label: getLocale() === "th" ? "ภาพผลงาน 4" : "Work image 4",
                size: 4,
              },
              {
                key: "work_image_fifth",
                label: getLocale() === "th" ? "ภาพผลงาน 5" : "Work image 5",
                size: 4,
              },
              {
                key: "work_image_sixth",
                label: getLocale() === "th" ? "ภาพผลงาน 6" : "Work image 6",
                size: 4,
              },
            ].map(({ key, label, size }) => (
              <Grid item xs={12} sm={size} key={key}>
                {isEdit ? (
                  checkNotEmpty(sellerData?.[key]) ? (
                    <WrapperOtherImage>
                      <img
                        src={
                          typeof sellerData[key] === "string"
                            ? sellerData[key]
                            : URL.createObjectURL(sellerData[key])
                        }
                        alt={key}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          cursor: "pointer",
                        }}
                      >
                        <Delete
                          onClick={() =>
                            setSellerData({ ...sellerData, [key]: "" })
                          }
                          sx={{
                            background: "#fff",
                            borderRadius: "100%",
                            color: "#e04132",
                            padding: "3px",
                          }}
                        />
                      </div>
                    </WrapperOtherImage>
                  ) : (
                    <DropZone
                      onDrop={(files) =>
                        setSellerData({ ...sellerData, [key]: files[0] })
                      }
                      accept=".jpg, .jpeg, .png, .webp"
                    >
                      {(props) => (
                        <ImageOtherEmpty
                          style={{ cursor: "pointer", flexDirection: "column" }}
                          {...props}
                        >
                          <PhotoCameraOutlined
                            sx={{
                              color: "#BCBCC0",
                              width: "25%",
                              height: "25%",
                            }}
                          />
                          <span
                            style={{ fontSize: "0.65rem", color: "#6C6C70" }}
                          >
                            อัพโหลดรูปภาพ
                          </span>
                        </ImageOtherEmpty>
                      )}
                    </DropZone>
                  )
                ) : checkNotEmpty(sellerData?.[key]) ? (
                  <WrapperOtherImage>
                    <img src={sellerData[key]} alt={key} />
                  </WrapperOtherImage>
                ) : (
                  <ImageOtherEmpty>
                    <InsertPhotoIcon
                      sx={{ color: "#d8d8dc", width: "25%", height: "25%" }}
                    />
                  </ImageOtherEmpty>
                )}
                <GalleryLabel>{label}</GalleryLabel>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* ─── Terms Accordion ─── */}
        <div style={{ marginBottom: "20px" }}>
          <StyledAccordion>
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="term-condition-content"
              id="term-condition-header"
            >
              <span
                style={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "#252525",
                }}
              >
                สัญญา ข้อกำหนดและเงื่อนไขการเข้าเป็นร้านค้ากับ "Xsurface"
              </span>
            </StyledAccordionSummary>

            <AccordionDetails>
              <TermsComponent userData={sellerData} hideHeader={true} />
            </AccordionDetails>
          </StyledAccordion>
        </div>

        {/* ─── Reject reason ─── */}
        {sellerData.is_approve === 2 ? (
          <Card>
            <SpanReject>
              {getLocale() === "th" ? "เหตุผลการปฎิเสธ" : "Reason to rejected"}
            </SpanReject>
            <p style={{ fontSize: "0.9375rem", color: "#252525", margin: 0 }}>
              {sellerData?.reject_seller_message === "" ||
              sellerData?.reject_seller_message === "null" ||
              sellerData?.reject_seller_message === null
                ? "-"
                : sellerData?.reject_seller_message}
            </p>
          </Card>
        ) : null}

        {/* ─── Approve / Reject buttons ─── */}
        {!isEdit && checkAllowPermission("sellers.can_approve") ? (
          sellerData?.is_approve === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {sellerData?.is_approve === 0 && isRemark === false ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 12,
                  }}
                >
                  <StyledButton
                    variant="contained"
                    sx={{ minWidth: 100 }}
                    onClick={() => {
                      openDialog({
                        type: "confirm",
                        title:
                          getLocale() === "th"
                            ? `ต้องการอนุมัติผู้ขายหรือไม่ ?`
                            : `Want to approve the seller?`,
                        onConfirm: () => {
                          handleSaveApprove();
                        },
                        maxWidth: 342,
                      });
                    }}
                  >
                    Approve
                  </StyledButton>

                  <StyledButton
                    variant="contained"
                    sx={{ minWidth: 100 }}
                    onClick={() => setIsRemark(true)}
                  >
                    Reject
                  </StyledButton>
                </div>
              ) : (
                <Card>
                  <WrapperTextField>
                    <TextField
                      label="เพิ่มเหตุผลการปฎิเสธ"
                      variant="outlined"
                      className="TextField-without-border-radius"
                      sx={{ m: 1, width: "98%" }}
                      multiline
                      rows={6}
                      value={comment}
                      onChange={(text) => setComment(text.target.value)}
                    />
                  </WrapperTextField>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 12,
                      marginTop: 8,
                    }}
                  >
                    <StyledButton
                      variant="outlined"
                      sx={{ minWidth: 100 }}
                      onClick={() => {
                        setIsRemark(false);
                        setComment("");
                      }}
                    >
                      cancel
                    </StyledButton>

                    <StyledButton
                      variant="contained"
                      sx={{ minWidth: 100 }}
                      onClick={() => {
                        openDialog({
                          type: "confirm",
                          title:
                            getLocale() === "th"
                              ? `ต้องการปฏิเสธผู้ขายหรือไม่ ?`
                              : `Want to reject the seller?`,
                          onConfirm: () => handleSaveReject(),
                          maxWidth: 342,
                        });
                      }}
                    >
                      confirm
                    </StyledButton>
                  </div>
                </Card>
              )}
            </div>
          ) : sellerData?.status_update === 1 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 16,
              }}
            >
              <StyledButton
                variant="contained"
                sx={{ minWidth: 100 }}
                onClick={() => {
                  openDialog({
                    type: "confirm",
                    title:
                      getLocale() === "th"
                        ? `ต้องการอนุมัติผู้ขายหรือไม่ ?`
                        : `Want to approve the seller?`,
                    onConfirm: () => {
                      handleSaveApprove();
                    },
                    maxWidth: 342,
                  });
                }}
              >
                Approve
              </StyledButton>
            </div>
          ) : null
        ) : null}
      </PageContainer>
    </Main>
  );
};
export default SellerDetail;
